// The colour control of a coloured tool (unify-ui ship 4b, D3: name-tag's whole one) — the palettes as colour strips, one
// row per part with its swatch and the colour's name under it, and a tap that opens every filament and brand colour at
// once plus the visitor's own. One part, mounted by every coloured tool; it decides nothing about the document — it says
// what the visitor picked (onChange, onLook) and shows the colours the page hands it (set). Which part prints on which
// filament stays each tool's (R90); an own colour's slot is the tool's too (core slotColour, R99).
//
// Nothing of the core is imported (ruling R87, packages/ui/test/shell.test.js): the tool hands in `core`, the one object
// the core's `filaments` subpath exports for it — FILAMENTS, BRAND, filamentHint, nearestFilament.
//
//   const colours = createColours({ root, S, core, looksLabel, onChange, onLook, extra });
//     root        an empty element: the control builds the palettes and the rows inside it
//     S           the page's words (its composed table: the shared half holds customColor and customCaption)
//     looksLabel  the word over the palettes (each tool's own: "Colour sets", "Colour themes")
//     onChange(key, hex, { live })  a part's colour: always upper-case #RRGGBB (I9). live: true while the own-colour
//                 input is being dragged, false when it is let go and for a tap on a colour
//     onLook(key) a palette was tapped (the tool sets every part at once)
//     extra(part) → an element for that part's row, or nothing (name-tag: the band's width and height)
//   The choices open in the row's flow, under its swatch (they never cover another row); while they are open a tap on the
//   control outside them only closes them (R115). The swatch says it controls them (aria-controls, aria-expanded) — they are
//   a set of toggle buttons, not a menu, so no aria-haspopup (the scrutiny's Minor 4).
//   colours.set(parts, looks)   on every render of the tool:
//     parts  [{ key, label, value, off? }] — off: 'disabled' (the swatch cannot be pressed), 'hidden' (the row is not
//            there), 'veiled' (the row keeps its room, unseen and unreachable); none: the row is live
//     looks  [{ key, name, colours: { [part key]: hex } }] — the tool's palettes in one shape (I9); [] for none
//   colours.close()             close any open choice
import { captionLines, swatchName, litLook, normalHex, rowsKey, looksKey } from './colours-model.js';
import { esc } from './esc.js';

let made = 0;   // one id stem per control on the page: a row's choices are named by it (aria-controls)
export function createColours({ root, S, core, looksLabel, onChange, onLook, extra = () => null }) {
  let parts = [], looks = [], rowsAt = null, looksAt = null;
  const stem = `ui-colours-${++made}`;
  root.classList.add('ui-colours');
  const looksEl = document.createElement('div'); looksEl.className = 'ui-looks';
  const rowsEl = document.createElement('div'); rowsEl.className = 'ui-colour-rows';
  root.replaceChildren(looksEl, rowsEl);
  const caption = (hex) => captionLines(hex, S, core).map((l) => `<span class="ui-cap-line">${esc(l)}</span>`).join('');
  const option = (c, title) => `<button type="button" class="ui-swatch-opt" data-hex="${esc(c.hex)}" title="${esc(title)}" aria-label="${esc(title)}" aria-pressed="false" style="background:${esc(c.hex)}"></button>`;

  function buildLooks() {
    looksEl.hidden = !looks.length;
    looksEl.innerHTML = looks.length ? `<span class="ui-looks-label">${esc(looksLabel || '')}</span><div class="ui-looks-row">${looks.map((l) => `<button type="button" class="ui-look" data-look="${esc(l.key)}" aria-pressed="false">
      <span class="ui-look-strip" aria-hidden="true">${Object.values(l.colours).map((c) => `<span style="background:${esc(c)}"></span>`).join('')}</span><span class="ui-look-name">${esc(l.name)}</span></button>`).join('')}</div>` : '';
  }
  function buildRows() {
    rowsEl.replaceChildren(...parts.map((p) => {
      const row = document.createElement('div');
      row.className = 'ui-colour'; row.dataset.part = p.key;
      row.innerHTML = `<span class="ui-colour-label">${esc(p.label)}</span>
        <button type="button" class="ui-swatch-btn" aria-expanded="false" aria-controls="${esc(`${stem}-${p.key}`)}"><span class="ui-swatch-chip"></span><span class="ui-swatch-caption"></span></button>
        <div class="ui-swatch-pop hidden" id="${esc(`${stem}-${p.key}`)}">
          <div class="ui-swatch-group">${core.FILAMENTS.map((f) => option(f, f.name)).join('')}</div>
          <div class="ui-swatch-group ui-swatch-group--brand">${core.BRAND.map((b) => option(b, `${b.name} ≈ ${b.nearest}`)).join('')}</div>
          <label class="ui-swatch-own">${esc(S.customColor)}<input type="color" value="#000000"></label>
        </div>`;
      const more = extra(p);
      // the tool's own controls come after the choices in the document as on the screen (the scrutiny's Minor 2: the tab order
      // follows what is drawn — swatch, choices, then the row's own controls)
      if (more) { const box = document.createElement('div'); box.className = 'ui-colour-extra'; box.append(more); row.append(box); }
      return row;
    }));
  }
  // paint every row from `parts` (its colour, caption, name, pressed choice, off state) and the palettes' lit one
  function paint() {
    for (const p of parts) {
      const row = rowsEl.querySelector(`.ui-colour[data-part="${CSS.escape(p.key)}"]`), hex = normalHex(p.value) || '#000000';
      const btn = row.querySelector('.ui-swatch-btn');
      row.querySelector('.ui-swatch-chip').style.background = hex;
      row.querySelector('.ui-swatch-caption').innerHTML = caption(hex);
      btn.setAttribute('aria-label', swatchName(p.label, hex, S, core));
      for (const o of row.querySelectorAll('.ui-swatch-opt')) o.setAttribute('aria-pressed', String(o.dataset.hex === hex));
      const own = row.querySelector('input[type="color"]'); if (own !== document.activeElement) own.value = hex.toLowerCase();
      btn.disabled = p.off === 'disabled';
      row.hidden = p.off === 'hidden';
      const veiled = p.off === 'veiled';
      row.classList.toggle('veiled', veiled); row.toggleAttribute('inert', veiled);
      if (veiled) row.setAttribute('aria-hidden', 'true'); else row.removeAttribute('aria-hidden');
      if (p.off) closeIn(row);
    }
    const lit = litLook(parts, looks);
    for (const b of looksEl.querySelectorAll('.ui-look')) { const on = b.dataset.look === lit; b.classList.toggle('on', on); b.setAttribute('aria-pressed', String(on)); }
  }
  // B1 (the scrutiny): choices that close while they hold the keyboard's focus (a pick, Escape) hand it back to their row's
  // swatch — a hidden choice cannot keep it, and the next Tab would start again at the top of the page
  function closeIn(row) {
    const pop = row.querySelector('.ui-swatch-pop'), btn = row.querySelector('.ui-swatch-btn'), held = pop.contains(document.activeElement);
    pop.classList.add('hidden');
    btn.setAttribute('aria-expanded', 'false');
    if (held && !btn.disabled && !row.hidden && !row.classList.contains('veiled')) btn.focus();
  }
  const close = () => rowsEl.querySelectorAll('.ui-colour').forEach(closeIn);
  // In two columns the panel scrolls on its own and clips what opens below its visible end (the bar's top, or its own
  // bottom edge): the choices are brought into that band by scrolling the PANEL by just what is needed. On a phone the
  // panel does not scroll (the page does): nothing here. (name-tag's revealInPanel, R38, by the shell's classes.)
  function revealInPanel(pop) {
    const panel = root.closest('.ui-panel'), bar = document.querySelector('.ui-bar');
    if (!panel || getComputedStyle(panel).overflowY !== 'auto') return;
    const end = Math.min(panel.getBoundingClientRect().bottom, bar ? bar.getBoundingClientRect().top : Infinity), over = pop.getBoundingClientRect().bottom - end;
    if (over > 0) panel.scrollTop += over;
  }
  const keyOf = (el) => el.closest('.ui-colour').dataset.part;
  // R115 (the fold): while a row's choices are open, a tap on the control outside them — another row's swatch, a palette, a
  // row's word — closes them and does nothing else (the visitor taps again for what they meant). Their own swatch still closes
  // them (the toggle below). A tap elsewhere on the page closes them and goes on to what it was for (the document listener).
  root.addEventListener('click', (e) => {
    const open = rowsEl.querySelector('.ui-swatch-pop:not(.hidden)');
    if (!open || open.contains(e.target) || open.closest('.ui-colour').querySelector('.ui-swatch-btn').contains(e.target)) return;
    close(); e.stopImmediatePropagation(); e.preventDefault();
  }, true);
  root.addEventListener('click', (e) => {
    const look = e.target.closest('.ui-look');
    if (look) { close(); onLook(look.dataset.look); return; }
    const btn = e.target.closest('.ui-swatch-btn');
    if (btn) {
      const row = btn.closest('.ui-colour'), pop = row.querySelector('.ui-swatch-pop'), willOpen = pop.classList.contains('hidden');
      close();
      if (willOpen) { pop.classList.remove('hidden'); btn.setAttribute('aria-expanded', 'true'); revealInPanel(pop); }
      return;
    }
    const opt = e.target.closest('.ui-swatch-opt');
    if (opt) { const key = keyOf(opt); close(); onChange(key, normalHex(opt.dataset.hex), { live: false }); }
  });
  // the own colour: every step of the drag is shown (live), the value let go of is the change
  root.addEventListener('input', (e) => { if (e.target.matches('input[type="color"]')) onChange(keyOf(e.target), normalHex(e.target.value), { live: true }); });
  root.addEventListener('change', (e) => { if (e.target.matches('input[type="color"]')) onChange(keyOf(e.target), normalHex(e.target.value), { live: false }); });
  document.addEventListener('click', (e) => { if (!root.contains(e.target)) close(); });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });

  return {
    set(nextParts, nextLooks = []) {
      parts = nextParts; looks = nextLooks;
      const rk = rowsKey(parts), lk = looksKey(looks);
      if (rk !== rowsAt) { buildRows(); rowsAt = rk; }
      if (lk !== looksAt) { buildLooks(); looksAt = lk; }
      paint();
    },
    close,
  };
}
